/**
 * User Service
 *
 * API client for user operations
 */

import { get, post, tokenStorage } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  RadiologistWithWorkload,
  RadiologistsResponse,
  UserWithInstitution,
  CreateUserData,
  UpdateUserData,
  ResetPasswordData,
  UserSearchFilters,
  UserListResponse,
} from '@/domain/user/types'

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get paginated list of users with filters
 */
export async function getUsers(filters?: UserSearchFilters): Promise<UserListResponse> {
  const params = new URLSearchParams()

  if (filters?.search) params.append('search', filters.search)
  if (filters?.role) params.append('role', filters.role)
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))
  if (filters?.institutionId) params.append('institutionId', filters.institutionId)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.USERS.BASE}?${queryString}`
    : API_ROUTES.USERS.BASE

  return get<UserListResponse>(url)
}

/**
 * Get user by ID
 */
export async function getUserById(id: string): Promise<UserWithInstitution> {
  return get<UserWithInstitution>(API_ROUTES.USERS.BY_ID(id))
}

/**
 * Create new user
 */
export async function createUser(
  data: CreateUserData
): Promise<{ user: UserWithInstitution; message: string }> {
  return post<{ user: UserWithInstitution; message: string }>(API_ROUTES.USERS.BASE, data)
}

/**
 * Update user (POST for WAF compatibility)
 */
export async function updateUser(
  id: string,
  data: UpdateUserData
): Promise<UserWithInstitution> {
  return post<UserWithInstitution>(API_ROUTES.USERS.UPDATE(id), data)
}

/**
 * Delete user (soft delete) - POST for WAF compatibility
 */
export async function deleteUser(id: string): Promise<void> {
  return post(API_ROUTES.USERS.DELETE(id), {})
}

/**
 * Reset user password
 */
export async function resetUserPassword(
  id: string,
  data: ResetPasswordData
): Promise<{ message: string }> {
  return post<{ message: string }>(API_ROUTES.USERS.RESET_PASSWORD(id), data)
}

/**
 * Activate user
 */
export async function activateUser(id: string): Promise<{ message: string }> {
  return post<{ message: string }>(API_ROUTES.USERS.ACTIVATE(id), {})
}

/**
 * Deactivate user
 */
export async function deactivateUser(id: string): Promise<{ message: string }> {
  return post<{ message: string }>(API_ROUTES.USERS.DEACTIVATE(id), {})
}

/**
 * Get list of active radiologists with their workload
 * Used for assignment dropdown
 */
export async function getRadiologists(): Promise<RadiologistWithWorkload[]> {
  const response = await get<RadiologistsResponse>(API_ROUTES.USERS.RADIOLOGISTS)
  return response.radiologists
}

/**
 * Get total workload for a radiologist
 */
export function getTotalWorkload(radiologist: RadiologistWithWorkload): number {
  return radiologist.pendingCount + radiologist.inProgressCount
}

/**
 * Sort radiologists by workload (least busy first)
 */
export function sortByWorkload(radiologists: RadiologistWithWorkload[]): RadiologistWithWorkload[] {
  return [...radiologists].sort((a, b) => getTotalWorkload(a) - getTotalWorkload(b))
}

// =============================================================================
// Profile API Functions
// =============================================================================

export interface ProfileData {
  id: string
  email: string
  role: string
  isActive: boolean
  institutionId: string | null
  institutionName: string | null
  lastLoginAt: string | null
  createdAt: string
  twoFactorEnabled: boolean
}

export interface UpdateProfileData {
  email?: string
}

export interface ChangePasswordData {
  currentPassword: string
  newPassword: string
}

/**
 * Get current user profile
 */
export async function getMyProfile(): Promise<ProfileData> {
  return get<ProfileData>(API_ROUTES.USERS.ME)
}

/**
 * Update current user profile (POST for WAF compatibility)
 */
export async function updateMyProfile(
  data: UpdateProfileData
): Promise<{ id: string; email: string; message: string }> {
  return post<{ id: string; email: string; message: string }>(API_ROUTES.USERS.ME_UPDATE, data)
}

/**
 * Cambia la contrasena del usuario actual y se queda con la sesion nueva.
 *
 * El servidor revoca todas las sesiones al cambiar la contrasena, incluida la
 * de quien la cambia, y a cambio devuelve credenciales nuevas. Guardarlas es
 * parte de la operacion, no un detalle de la pantalla que la invoca: si se
 * omite, el navegador sigue usando el token anterior y se queda fuera.
 *
 * Con una contrasena temporal el efecto es inmediato, porque el token viejo
 * lleva escrito `mustChangePassword` y el servidor rechaza cada peticion.
 * Con un cambio voluntario tarda lo que dure el token de acceso, y entonces
 * el refresco falla porque su token ya no existe.
 */
export async function changeMyPassword(
  data: ChangePasswordData
): Promise<{ message: string }> {
  const respuesta = await post<{
    message: string
    accessToken?: string
    refreshToken?: string
  }>(API_ROUTES.USERS.CHANGE_PASSWORD, data)

  if (respuesta.accessToken && respuesta.refreshToken) {
    tokenStorage.setTokens(respuesta.accessToken, respuesta.refreshToken)
  }

  return { message: respuesta.message }
}
